import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services/api';
import { messagingService, type ChannelStatus, type WhatsAppPlaceholderInput } from '@/services/messaging';
import { formatDateTime } from '@/utils';

/**
 * Broker settings -> "Messaging channels". The broker's own Telegram bot
 * (connect / test / rotate / disconnect) and the WhatsApp placeholder, which
 * is stored and validated but sends nothing yet. Tokens are write-only: the
 * page shows a masked hint and never the token.
 */

const BADGE: Record<ChannelStatus, string> = {
  connected: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  failing: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  not_set: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  not_active: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};
const card = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4';
const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const primary =
  'px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50';
const secondary =
  'px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200';

const EMPTY_WA: WhatsAppPlaceholderInput = {
  wa_provider: 'meta_cloud', wa_display_number: '', wa_phone_number_id: '', wa_business_account_id: '',
  wa_access_token: '', wa_verify_token: '',
};

/** The WhatsApp fields the server echoes back (the tokens are write-only and never come back). */
type WaSynced = Pick<
  WhatsAppPlaceholderInput,
  'wa_provider' | 'wa_display_number' | 'wa_phone_number_id' | 'wa_business_account_id'
>;

function Badge({ status, label }: { status: ChannelStatus; label: string }) {
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE[status]}`}>{label}</span>;
}

function when(value: string | null): string {
  return value ? formatDateTime(value) : '—';
}

export function MessagingChannelsSection() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['messagingChannels'],
    queryFn: messagingService.getChannels,
  });
  const [token, setToken] = useState('');
  const [wa, setWa] = useState<WhatsAppPlaceholderInput>(EMPTY_WA);

  // What the fields last took from the server. Every Telegram action refetches this one shared query,
  // so a refetch must never overwrite a WhatsApp field the operator has edited and not saved yet:
  // only a field still showing the last server value follows the new one.
  const syncedWa = useRef<WaSynced | null>(null);
  useEffect(() => {
    if (!data) return;
    const server: WaSynced = {
      wa_provider: data.whatsapp.wa_provider || 'meta_cloud',
      wa_display_number: data.whatsapp.wa_display_number,
      wa_phone_number_id: data.whatsapp.wa_phone_number_id,
      wa_business_account_id: data.whatsapp.wa_business_account_id,
    };
    const previous = syncedWa.current;
    syncedWa.current = server;
    setWa((current) => {
      const follow = (field: keyof WaSynced) => previous === null || current[field] === previous[field];
      return {
        ...current,
        wa_provider: follow('wa_provider') ? server.wa_provider : current.wa_provider,
        wa_display_number: follow('wa_display_number') ? server.wa_display_number : current.wa_display_number,
        wa_phone_number_id: follow('wa_phone_number_id') ? server.wa_phone_number_id : current.wa_phone_number_id,
        wa_business_account_id: follow('wa_business_account_id')
          ? server.wa_business_account_id
          : current.wa_business_account_id,
      };
    });
  }, [data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['messagingChannels'] });
  const onError = (err: unknown) => toast.error(getErrorMessage(err));

  const connect = useMutation({
    mutationFn: (value: string) => messagingService.connectTelegram(value),
    onSuccess: () => {
      setToken('');
      toast.success('Telegram bot connected');
      void refresh();
    },
    onError,
  });
  const check = useMutation({
    mutationFn: () => messagingService.testTelegram(),
    onSuccess: (result) => {
      if (result.ok) toast.success(`@${result.bot_username} is working`);
      else toast.error(result.last_error_message || 'The bot is not receiving messages.');
      void refresh();
    },
    onError,
  });
  const rotate = useMutation({
    mutationFn: () => messagingService.rotateTelegram(),
    onSuccess: () => {
      toast.success('Webhook secrets rotated');
      void refresh();
    },
    onError,
  });
  const disconnect = useMutation({
    mutationFn: () => messagingService.disconnectTelegram(),
    onSuccess: () => {
      toast.success('Telegram bot disconnected');
      void refresh();
    },
    onError,
  });
  const saveWa = useMutation({
    mutationFn: (input: WhatsAppPlaceholderInput) => messagingService.saveWhatsApp(input),
    onSuccess: () => {
      setWa((current) => ({ ...current, wa_access_token: '', wa_verify_token: '' }));
      toast.success('WhatsApp details saved');
      void refresh();
    },
    onError,
  });

  if (isLoading) return <p role="status" className="text-sm text-gray-500">Loading messaging channels…</p>;
  if (isError || !data) {
    return (
      <p role="alert" className="text-sm text-red-600">
        Could not load messaging channels. {getErrorMessage(error)}
      </p>
    );
  }

  const tg = data.telegram;
  const whatsapp = data.whatsapp;
  const connected = tg.status === 'connected' || tg.status === 'failing';
  const setField = (field: keyof WhatsAppPlaceholderInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setWa((current) => ({ ...current, [field]: e.target.value }));

  return (
    <div className="space-y-6">
      <section aria-labelledby="tg-heading" className={card}>
        <div className="flex items-center justify-between">
          <h3 id="tg-heading" className="text-lg font-medium text-gray-900 dark:text-white">Telegram</h3>
          <Badge status={tg.status} label={tg.status_display} />
        </div>
        {tg.bot_username && (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Bot:{' '}
            <a href={tg.bot_link} target="_blank" rel="noopener noreferrer" className="text-blue-600">
              @{tg.bot_username}
            </a>{' '}
            · token {tg.token_hint}
          </p>
        )}
        {tg.status === 'failing' && tg.last_error && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-300">
            Last error ({when(tg.last_error_at)}): {tg.last_error}
          </p>
        )}
        {connected && (
          <p className="text-xs text-gray-500">Last message delivered: {when(tg.last_delivery_at)}</p>
        )}
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (token.trim()) connect.mutate(token.trim());
          }}
        >
          <label htmlFor="tg-token" className={labelClass}>
            {connected ? 'Replace the bot token' : 'Bot token from @BotFather'}
          </label>
          <input
            id="tg-token"
            type="password"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className={inputClass}
            placeholder="123456789:AA..."
          />
          <p className="text-xs text-gray-500">
            Create a bot with @BotFather in Telegram and paste its token here. It is stored encrypted and never shown again.
          </p>
          <button type="submit" disabled={!token.trim() || connect.isPending} className={primary}>
            {connect.isPending ? 'Connecting…' : 'Connect bot'}
          </button>
        </form>
        {connected && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={secondary} disabled={check.isPending} onClick={() => check.mutate()}>
              Test
            </button>
            <button type="button" className={secondary} disabled={rotate.isPending} onClick={() => rotate.mutate()}>
              Rotate secrets
            </button>
            <button
              type="button"
              className={secondary}
              disabled={disconnect.isPending}
              onClick={() => {
                if (window.confirm('Disconnect this bot? People messaging it will stop getting replies.')) {
                  disconnect.mutate();
                }
              }}
            >
              Disconnect
            </button>
          </div>
        )}
      </section>

      <section aria-labelledby="wa-heading" className={card}>
        <div className="flex items-center justify-between">
          <h3 id="wa-heading" className="text-lg font-medium text-gray-900 dark:text-white">WhatsApp</h3>
          <Badge status="not_active" label="Not active yet" />
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Save your WhatsApp Business details now. Nothing is sent over WhatsApp until the WhatsApp channel is switched on.
        </p>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            saveWa.mutate(wa);
          }}
        >
          <div>
            <label htmlFor="wa-provider" className={labelClass}>Provider</label>
            <select id="wa-provider" value={wa.wa_provider} onChange={setField('wa_provider')} className={inputClass}>
              <option value="meta_cloud">Meta Cloud API</option>
              <option value="gupshup">Gupshup</option>
            </select>
          </div>
          <div>
            <label htmlFor="wa-number" className={labelClass}>Business WhatsApp number</label>
            <input id="wa-number" value={wa.wa_display_number} onChange={setField('wa_display_number')} className={inputClass} />
          </div>
          <div>
            <label htmlFor="wa-phone-id" className={labelClass}>Phone-number ID</label>
            <input id="wa-phone-id" value={wa.wa_phone_number_id} onChange={setField('wa_phone_number_id')} className={inputClass} />
          </div>
          <div>
            <label htmlFor="wa-waba" className={labelClass}>WhatsApp Business Account ID</label>
            <input id="wa-waba" value={wa.wa_business_account_id} onChange={setField('wa_business_account_id')} className={inputClass} />
          </div>
          <div>
            <label htmlFor="wa-token" className={labelClass}>Access token</label>
            <input
              id="wa-token"
              type="password"
              autoComplete="off"
              value={wa.wa_access_token}
              onChange={setField('wa_access_token')}
              className={inputClass}
              placeholder={whatsapp.wa_access_token_hint ? `Saved (${whatsapp.wa_access_token_hint}) — leave blank to keep` : ''}
            />
          </div>
          <div>
            <label htmlFor="wa-verify" className={labelClass}>Webhook verify token</label>
            <input
              id="wa-verify"
              type="password"
              autoComplete="off"
              value={wa.wa_verify_token}
              onChange={setField('wa_verify_token')}
              className={inputClass}
              placeholder={whatsapp.wa_verify_token_set ? 'Saved — leave blank to keep' : ''}
            />
          </div>
          <div>
            <button type="submit" disabled={saveWa.isPending} className={primary}>
              {saveWa.isPending ? 'Saving…' : 'Save WhatsApp details'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default MessagingChannelsSection;
